import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles, Home, Search, PlusCircle, User, Shield, MessageCircle, Calendar, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import useOverlayGate from '../hooks/useOverlayGate';

const AppTour = () => {
  const { user, isAdmin, t, language } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [wantTour, setWantTour] = useState(false);
  const gate = useOverlayGate('app-tour', 70);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hoibuur_tour_completed');
    if (!hasSeenTour && user) {
      setTimeout(() => setWantTour(true), 1000);
    }
  }, [user]);

  useEffect(() => {
    if (wantTour) gate.request();
    else gate.release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantTour]);

  const showTour = wantTour && gate.canShow;
  const setShowTour = setWantTour;

  const en = language === 'en';

  const steps = [
    {
      title: en ? 'Welcome to Hoi Buur!' : 'Welkom bij Hoi Buur!',
      description: en ? 'Great to have you here! This is your platform to meet neighbors and discover fun activities in Almere. Let us give you a quick tour.' : 'Fijn dat je er bent! Dit is jouw platform om buren te ontmoeten en leuke activiteiten te ontdekken in Almere. Laten we je snel rondleiden door alle functies.',
      icon: Home,
      iconColor: 'text-brand',
      highlight: 'home'
    },
    {
      title: en ? 'Your Home Page' : 'Je Startpagina',
      description: en ? 'On the homepage you see an overview of activities in your neighborhood. Use filters to search by neighborhood, type, date or age group.' : 'Op de homepage zie je een overzicht van activiteiten in jouw buurt. Gebruik de filters om te zoeken op wijk, type activiteit, datum of leeftijdsgroep.',
      icon: Home,
      iconColor: 'text-brand',
      highlight: 'home-feed'
    },
    {
      title: en ? 'Discover' : 'Ontdekken',
      description: en ? 'Go to "Discover" to:\n- Search and filter activities\n- Find neighbors who want to connect\n- Explore neighborhoods in Almere' : 'Ga naar "Ontdekken" om:\n- Activiteiten te zoeken en filteren\n- Buren te vinden die willen connecten\n- Wijken in Almere te verkennen',
      icon: Search,
      iconColor: 'text-accent',
      highlight: 'discover'
    },
    {
      title: en ? 'Add Something Fun' : 'Voeg Iets Leuks Toe',
      description: en ? 'Organize something fun! Click "Add" to:\n- Plan a walk, cooking night or sports event\n- Set it up as recurring activity\n- Invite neighbors' : 'Organiseer zelf iets leuks! Klik op "Voeg toe" om:\n- Een wandeling, kookavond of sportmoment te plannen\n- Het als terugkerende activiteit in te stellen\n- Buren uit te nodigen',
      icon: PlusCircle,
      iconColor: 'text-green-500',
      highlight: 'create'
    },
    {
      title: en ? 'Join & Share Activities' : 'Meedoen & Delen',
      description: en ? 'For each activity you can:\n- Join - sign up as participant\n- Like - show your interest\n- Share - via WhatsApp, Facebook, etc.\n- Comment and add photos' : 'Bij elke activiteit kun je:\n- Meedoen - meld je aan als deelnemer\n- Liken - toon je interesse\n- Delen - via WhatsApp, Facebook, etc.\n- Reageren en fotos toevoegen',
      icon: Heart,
      iconColor: 'text-red-500',
      highlight: 'activity-actions'
    },
    {
      title: en ? 'Set Up Your Profile' : 'Je Profiel Instellen',
      description: en ? 'Complete your profile:\n- Add a photo and bio\n- Set your age and availability\n- Select your interests\n- Enable "Want to connect"\nSo neighbors can find and match with you!' : 'Maak je profiel compleet:\n- Voeg een profielfoto en bio toe\n- Geef je leeftijd en beschikbaarheid aan\n- Selecteer je interesses\n- Zet "Wil connecten" aan\nZo kunnen buren jou vinden!',
      icon: User,
      iconColor: 'text-purple-500',
      highlight: 'profile'
    },
    ...(isAdmin ? [{
      title: en ? 'Admin Dashboard' : 'Admin Dashboard',
      description: en ? 'As admin you have access to:\n- Moderation - approve/reject activities\n- Analytics - view statistics\n- User management\n- Alerts - receive notifications\n- Tools - manage data and integrations' : 'Als admin heb je toegang tot:\n- Moderatie - keur activiteiten goed/af\n- Analytics - bekijk statistieken\n- Gebruikersbeheer\n- Alerts - ontvang meldingen\n- Tools - beheer data en integraties',
      icon: Shield,
      iconColor: 'text-yellow-500',
      highlight: 'admin'
    }] : []),
    {
      title: en ? 'Ready to Start!' : 'Klaar om te Starten!',
      description: en ? 'You are all set! Discover activities, meet neighbors and become part of the Almere community.\n\nTips:\n- Check activities in your neighborhood first\n- Complete your profile\n- Invite friends via the share feature\n\nEnjoy!' : 'Je bent helemaal klaar! Ontdek activiteiten, ontmoet buren en maak deel uit van de Almere community.\n\nTips:\n- Bekijk eerst de activiteiten in jouw wijk\n- Vul je profiel compleet in\n- Nodig vrienden uit via de deel-functie\n\nVeel plezier!',
      icon: Sparkles,
      iconColor: 'text-brand',
      highlight: null
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    setShowTour(false);
    gate.release();
    localStorage.setItem('hoibuur_tour_completed', 'true');
  };

  const skipTour = () => {
    completeTour();
  };

  if (!showTour || !user) return null;

  const currentStepData = steps[currentStep];
  const IconComponent = currentStepData.icon;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998] animate-fade-in"
        onClick={skipTour}
        data-testid="app-tour-overlay"
      />
      
      {/* Tour Card - Positioned higher to avoid bottom navigation */}
      <Card className="fixed inset-x-4 top-[15%] mx-auto z-[9999] max-w-md p-4 sm:p-6 rounded-3xl bg-white shadow-2xl animate-fade-in-up max-h-[65vh] overflow-y-auto mb-24" data-testid="app-tour-card">
        {/* Close Button */}
        <button 
          onClick={skipTour}
          className="absolute top-3 right-3 text-muted-foreground hover:text-primary transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress Dots */}
        <div className="flex justify-center gap-1.5 mb-4">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all ${
                index === currentStep 
                  ? 'w-5 bg-brand' 
                  : index < currentStep 
                    ? 'w-1.5 bg-brand/50' 
                    : 'w-1.5 bg-secondary'
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="text-center mb-3">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand/10`}>
            <IconComponent className={`w-7 h-7 ${currentStepData.iconColor}`} />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-2 mb-4">
          <h3 className="font-heading font-bold text-lg text-primary">
            {currentStepData.title}
          </h3>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line text-sm">
            {currentStepData.description}
          </p>
        </div>

        {/* Step Counter */}
        <p className="text-center text-xs text-muted-foreground mb-3">
          {en ? `Step ${currentStep + 1} of ${steps.length}` : `Stap ${currentStep + 1} van ${steps.length}`}
        </p>

        {/* Navigation Buttons */}
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button 
              variant="outline" 
              onClick={handlePrev}
              className="flex-1 rounded-full h-10 text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('common.back')}
            </Button>
          )}
          
          {currentStep === 0 && (
            <Button 
              variant="ghost" 
              onClick={skipTour}
              className="flex-1 rounded-full h-10 text-sm text-muted-foreground"
              data-testid="app-tour-skip-btn"
            >
              {en ? 'Skip' : 'Overslaan'}
            </Button>
          )}
          
          <Button 
            onClick={handleNext}
            className="flex-1 btn-primary h-10 text-sm"
            data-testid="app-tour-next-btn"
          >
            {currentStep < steps.length - 1 ? (
              <>
                {en ? 'Next' : 'Volgende'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1" />
                {en ? 'Start!' : 'Start!'}
              </>
            )}
          </Button>
        </div>
      </Card>
    </>
  );
};

export default AppTour;
